/**
 * Cliente de V4/V5 — del comentario suelto al CAMBIO con procedencia, y a lo
 * que ese cambio rompe en el resto del documento.
 *
 * V1 le puso dirección al comentario (de qué párrafo habla). V2 lo reconoció sin
 * IA. Lo que este módulo trae es el paso siguiente: «hay 99 comentarios» pasa a
 * «hay 14 cambios propuestos, cada uno con las voces que lo piden, y aprobar el
 * tercero obliga a mirar otros cinco párrafos».
 *
 * Cuatro reglas que esta capa NO puede aflojar, porque son las que hacen que la
 * pantalla no mienta:
 *
 *  · **D05/D06 — la IA no dirime.** Un cambio `contradictoria` NO trae redacción
 *    propuesta: trae `posiciones`, con las posturas enfrentadas y quién sostiene
 *    cada una. Eso no es un hueco ni un error del sistema: es su trabajo bien
 *    hecho. La decisión es de una persona, y hasta que esa persona escriba el
 *    texto final el servidor devuelve `puede_aprobarse: false`.
 *  · **C7 — todo lo que dice la IA cita su aporte.** Las voces salen de las citas
 *    que sobrevivieron a la validación; un cambio propuesto por IA sin ninguna
 *    cita viva viene marcado `no_respaldado` en vez de disimulado.
 *  · **D07/D08 — el impacto es un objeto con estado**, no un aviso. Cerrarlo sin
 *    cambiar nada exige razón escrita, y con impactos pendientes la versión no se
 *    cierra. Por eso `puede_cerrarse` y `motivo_cierre` viajan en dos respuestas.
 *  · **D25 — la IA va por lote.** {@link cambiosApi.sintetizar} pide UNA corrida
 *    para toda la validación. Nunca una por comentario. Lo que devuelve no es la
 *    síntesis sino el estado de la corrida: se espera con
 *    {@link cambiosApi.estadoCorrida} y recién después se lee el diff.
 *
 * Y el caso normal que NO es un error, igual que en `documentos.ts`: una
 * validación sin documento cargado responde 404 en todos estos endpoints. Las
 * tres validaciones vivas del Consejo están así. Acá se traduce a `null` para
 * que la página decida su modo con un `if` y no con un `try/catch`.
 */

import { ApiError, apiFetch } from './api';

// ═══════════════════════════════════════════════════════════════════════════
// 1 · Vocabulario
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Qué clase de acuerdo hay detrás de un cambio. Es lo primero que la síntesis
 * decide, antes de redactar una sola palabra, y es lo que gobierna la pantalla:
 * `contradictoria` no muestra propuesta, muestra posturas.
 */
export type ClaseCambio = 'convergente' | 'complementaria' | 'contradictoria' | 'sin_cambio';

/** Recorrido de un cambio. Todos nacen en `propuesto`, contradicciones incluidas. */
export type EstadoCambio =
  | 'propuesto'
  | 'en_discusion'
  | 'acordado'
  | 'aprobado'
  | 'aplicado'
  | 'descartado';

/** Qué hace una voz respecto del cambio que la cita. */
export type QueAporta = 'pidio' | 'respalda' | 'contradice' | 'pregunta';

/** Verbos de sala, no estados de tabla: se dice «lo aprobamos», no «pasalo a aprobado». */
export type AccionCambio = 'aprobar' | 'descartar' | 'en_discusion';

/** Estados de un impacto (SSOT §5.6). Solo `pendiente` bloquea el cierre. */
export type EstadoImpacto =
  | 'pendiente'
  | 'revisado_sin_cambio'
  | 'genera_cambio_derivado'
  | 'descartado';

// ═══════════════════════════════════════════════════════════════════════════
// 2 · La bandeja por sección
// ═══════════════════════════════════════════════════════════════════════════

/** Una voz tal como entró. El texto escrito manda; si no lo hay, lo dicho. */
export interface ComentarioDeSeccion {
  aporte_id: string;
  autor: string | null;
  rol: string | null;
  texto: string;
  tiene_audio: boolean;
  created_at: string;
  /** puntero (lo señaló su autor) · manual (lo ubicó quien facilita) · lexico (V2). */
  origen_ancla: string | null;
}

export interface ParrafoComentado {
  /** Null solo en el grupo `sin_ubicar`: no hay párrafo al que apuntar. */
  bloque_id_estable: string | null;
  orden: number;
  ref: string | null;
  texto: string;
  comentarios: ComentarioDeSeccion[];
}

export interface SeccionComentada {
  titulo: string;
  /** Del capítulo al título más específico. La pantalla decide cuánto mostrar. */
  ruta: string[];
  ruta_texto: string;
  nivel: number;
  /** titulo · encabezado · sin_ubicar */
  clase: string;
  orden_inicio: number;
  orden_fin: number;
  comentarios: number;
  parrafos_tocados: number;
  parrafos: ParrafoComentado[];
}

export interface BandejaSecciones {
  version_id: string;
  documento_titulo: string;
  comentarios_total: number;
  comentarios_ubicados: number;
  /** Voces que no cuelgan de ningún párrafo. No se descartan: van al final. */
  comentarios_sin_ubicar: number;
  secciones_con_voces: number;
  secciones: SeccionComentada[];
}

// ═══════════════════════════════════════════════════════════════════════════
// 3 · El diff de cuatro capas (§5.8)
// ═══════════════════════════════════════════════════════════════════════════

/** Capa 2: quién pidió esto, qué dijo y si quedó su audio. */
export interface VozDelCambio {
  aporte_id: string;
  autor: string | null;
  rol: string | null;
  texto: string;
  /** Con esto se va a escuchar el audio original: `GET /media/{aporte_id}`. */
  tiene_audio: boolean;
  cuando: string | null;
  que_aporta: QueAporta | null;
  peso: number | null;
}

export interface SostieneLaPostura {
  aporte_id: string;
  autor: string | null;
  rol: string | null;
  tiene_audio: boolean;
}

/**
 * Una de las posturas enfrentadas. Nunca un punto medio: promediar dos posturas
 * opuestas fabrica un consenso que nadie sostuvo.
 */
export interface PosicionEnfrentada {
  postura: string;
  sostienen: SostieneLaPostura[];
}

export interface CambioConProcedencia {
  id: string;
  bloque_id_estable: string;
  /** Numeración propia del documento: «RF-064». Como lo nombra la gente. */
  ref: string | null;
  orden: number | null;
  seccion: string | null;
  estado: EstadoCambio;
  clase: ClaseCambio;

  /** Capa 1 — el texto como está hoy en la versión congelada. */
  original: string;
  /** Capa 2 — las voces que lo piden. Vacío + `no_respaldado` es una alarma. */
  voces: VozDelCambio[];
  /** Capa 3 — lo que la IA propuso. NULL en una contradicción, a propósito. */
  propuesto: string | null;
  motivo: string | null;
  /** Capa 3 de una contradicción: las posturas, sin resolver. */
  posiciones: PosicionEnfrentada[];
  escalada: boolean;
  /** Capa 4 — la redacción aprobada, con nombre y fecha. */
  final: string | null;
  aprobado_por: string | null;
  aprobado_por_nombre: string | null;
  aprobado_at: string | null;

  /** ia · humano */
  propuesto_por: string;
  corrida_id: string | null;
  created_at: string;
  /** La síntesis dice que esto lo tiene que dirimir una persona. */
  exige_persona: boolean;
  /** Lo propuso la IA y ninguna cita resistió la validación (C7). No se aprueba así. */
  no_respaldado: boolean;
  /** Resuelto por la MISMA puerta que usa el servidor en el PATCH. */
  puede_aprobarse: boolean;
  /** Vacío si se puede aprobar. Si no, el motivo ya está escrito para la pantalla. */
  porque_no: string;
  impactos_abiertos: number;
  impactos_total: number;
}

export interface DiffDeValidacion {
  version_id: string;
  documento_titulo: string;
  total: number;
  por_clase: Partial<Record<ClaseCambio, number>>;
  por_estado: Partial<Record<EstadoCambio, number>>;
  impactos_pendientes: number;
  /** D08: con impactos pendientes la versión no se cierra ni se exporta. */
  puede_cerrarse: boolean;
  motivo_cierre: string;
}

/** El diff completo: cabecera + la lista, en orden de documento. */
export interface DiffConCambios extends DiffDeValidacion {
  cambios: CambioConProcedencia[];
}

// ═══════════════════════════════════════════════════════════════════════════
// 4 · El grafo y sus impactos
// ═══════════════════════════════════════════════════════════════════════════

export interface AristaDeContexto {
  id: string | null;
  /** referencia_cruzada · termino_compartido · … Se puede silenciar por tipo (R04). */
  tipo: string;
  /** determinista · inferida · declarada */
  origen: string;
  /** Por qué existe la relación, en una línea. Es el «porqué» del aviso. */
  evidencia: string | null;
  confianza: number | null;
}

export interface ImpactoAbierto {
  id: string;
  bloque_afectado: string;
  /** Cómo se llama ese párrafo en la sala: «RF-064». Un uuid no le dice nada a nadie. */
  etiqueta: string;
  orden: number | null;
  estado: EstadoImpacto;
  razon: string | null;
  /** 1 = a un salto del cambio. 2 = el cruce que a un salto no existe. */
  profundidad: number;
  arista: AristaDeContexto | null;
  /** Por qué estamos interrumpiendo a alguien, en una línea. */
  porque: string;
  cambio_derivado_id: string | null;
  resuelto_por: string | null;
  resuelto_por_nombre: string | null;
  resuelto_at: string | null;
  created_at: string;
}

/** Los impactos de UN cambio. Agrupados así porque así se deciden. */
export interface GrupoDeImpactos {
  cambio_id: string;
  bloque_id_estable: string;
  ref: string | null;
  clase: ClaseCambio;
  estado: EstadoCambio;
  /** La redacción que disparó los impactos. */
  texto: string;
  abiertos: number;
  impactos: ImpactoAbierto[];
}

export interface ImpactosDeValidacion {
  version_id: string;
  total: number;
  pendientes: number;
  puede_cerrarse: boolean;
  motivo_cierre: string;
  grupos: GrupoDeImpactos[];
}

/**
 * Qué abrió una aprobación, y qué NO abrió y por qué.
 *
 * Lo omitido se cuenta a propósito: si mañana alguien dice «el sistema no me
 * avisó», la respuesta tiene que ser un número. Dos contadores son SIEMPRE cero
 * por este camino y el backend lo documenta: `omitidos_por_silencio` (las
 * silenciadas se filtran una capa antes) y `omitidos_por_umbral` (el corte lo
 * aplica el recorrido, salto por salto).
 */
export interface Propagacion {
  creados: number;
  ya_estaban: number;
  profundidad: number;
  escalado: boolean;
  motivo: string;
  omitidos_por_umbral: number;
  omitidos_por_silencio: number;
  omitidos_por_ciclo: number;
  omitidos_por_profundidad: number;
  omitidos_por_tope: number;
  omitidos_por_bloque_ausente: number;
}

export interface ResultadoDecision {
  cambio: CambioConProcedencia;
  /** Solo al aprobar. Null al descartar o al dejar en discusión. */
  propagacion: Propagacion | null;
  puede_cerrarse: boolean;
  motivo_cierre: string;
}

export interface GrafoConstruido {
  version_id: string;
  insertadas: number;
  actualizadas: number;
  podadas: number;
  total: number;
  por_tipo: Record<string, number>;
  silenciadas: number;
  mensaje: string;
}

// ═══════════════════════════════════════════════════════════════════════════
// 5 · Entradas
// ═══════════════════════════════════════════════════════════════════════════

/** Cuerpo de `PATCH /cambios/{id}`. La redacción es editable: lo de la IA es borrador. */
export interface DecisionCambio {
  accion: AccionCambio;
  /** La redacción que se aprueba. En una contradicción es OBLIGATORIA. */
  texto_final?: string;
  /** Por qué se decidió así. Queda en el expediente. */
  motivo?: string;
  /**
   * Confianza mínima de cada salto al propagar. Sin esto se usa la calibración
   * del módulo de aristas, medida contra el documento real. Subirlo abre menos
   * impactos: es una decisión de quien facilita, no un detalle técnico.
   */
  minimo?: number;
  /** Cuántos saltos camina la propagación (1..3). Por defecto 2. */
  profundidad?: number;
}

/** Cuerpo de `PATCH /impactos/{id}`. */
export interface ResolucionImpacto {
  estado: Exclude<EstadoImpacto, 'pendiente'>;
  /** Obligatoria para cerrar sin cambiar nada (D07). Sin ella el servidor devuelve 422. */
  razon?: string;
  /** Solo con `genera_cambio_derivado`. */
  cambio_derivado_id?: string;
}

/** Estado de la corrida de síntesis. Lo que se espera, no la síntesis misma. */
export interface EstadoCorridaSintesis {
  corrida_id: string;
  tarea: string;
  /**
   * El vocabulario es el del motor (`services/ai/corridas.py`), no uno propio:
   * el estado de una corrida rota es `fallida`, nunca `error`. Un sinónimo acá
   * dejaría a {@link corridaTerminada} devolviendo `false` para siempre y la
   * Cabina preguntando cada cinco segundos por una corrida que ya murió.
   */
  estado:
    | 'en_cola'
    | 'corriendo'
    | 'ok'
    | 'fallida'
    | 'sin_cupo'
    | 'pendiente_reintento'
    | 'cancelada';
  /** Ya escrito en español y en segunda persona: se muestra tal cual. */
  mensaje: string;
  posicion: number;
  eta_seg: number | null;
  fragmentos: { total: number; listos: number };
  reintenta_at: string | null;
  version_id?: string;
  actividad_id?: string;
}

/** Estados de los que ya no se sale: dejar de preguntar. */
export function corridaTerminada(estado: EstadoCorridaSintesis['estado']): boolean {
  return estado === 'ok' || estado === 'fallida' || estado === 'cancelada';
}

// ═══════════════════════════════════════════════════════════════════════════
// 6 · Llaves de caché
// ═══════════════════════════════════════════════════════════════════════════

export const cambiosKeys = {
  secciones: (actividadId: string) => ['validacion-secciones', actividadId] as const,
  /**
   * Los cambios de una validación. Con un solo argumento devuelve la llave de la
   * lista completa, que es la que invalidan tanto el diff como el tablero de
   * impactos de la Cabina: dos llaves distintas para la misma lista harían que
   * aprobar en un panel dejara al otro mostrando datos viejos.
   */
  cambios: (actividadId: string, clase?: string, estado?: string) =>
    ['validacion-cambios', actividadId, clase ?? '', estado ?? ''] as const,
  /** Alias de {@link cambiosKeys.cambios}. Misma llave: invalidar una invalida la otra. */
  diff: (actividadId: string, clase?: string, estado?: string) =>
    ['validacion-cambios', actividadId, clase ?? '', estado ?? ''] as const,
  impactos: (actividadId: string, soloPendientes = false) =>
    ['validacion-impactos', actividadId, soloPendientes] as const,
  corrida: (corridaId: string) => ['corrida', corridaId] as const,
};

// ═══════════════════════════════════════════════════════════════════════════
// 7 · API
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Un 404 acá significa «esta validación no tiene documento», no un fallo. Se
 * traduce a `null` por la misma razón que en `documentos.ts`: la decisión de la
 * página es binaria y merece un `if`, no un try/catch. Cualquier otro error
 * (403, 422, 500, red) sigue subiendo.
 */
async function nullEn404<T>(promesa: Promise<T>): Promise<T | null> {
  try {
    return await promesa;
  } catch (e) {
    if (e instanceof ApiError && e.status === 404) return null;
    throw e;
  }
}

function query(params: Record<string, string | number | boolean | undefined>): string {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === '') continue;
    q.set(k, String(v));
  }
  const s = q.toString();
  return s ? `?${s}` : '';
}

export const cambiosApi = {
  /**
   * La bandeja del §5.7: qué se comentó, dónde, y quién lo dijo — agrupado por
   * sección. Es un GROUP BY, no una corrida de IA: no gasta cuota y responde en
   * milisegundos. `null` si la validación no tiene documento.
   *
   * Los comentarios sin ubicar NO desaparecen: vienen en un grupo final de clase
   * `sin_ubicar`. Cada uno es una voz humana con su audio.
   */
  secciones: (
    actividadId: string,
    opciones: { inferirJerarquia?: boolean } = {},
  ): Promise<BandejaSecciones | null> =>
    nullEn404(
      apiFetch<BandejaSecciones>(
        `/validaciones/${actividadId}/secciones${query({
          inferir_jerarquia: opciones.inferirJerarquia,
        })}`,
      ),
    ),

  /**
   * Calcula y guarda qué párrafo depende de cuál. Determinista, idempotente y
   * gratis: detectar que dos párrafos hablan de lo mismo es comparar términos,
   * no razonar. Respeta lo que alguien silenció — un recálculo que lo pisara
   * devolvería la sala a la fatiga de alertas (R04). Solo quien facilita.
   */
  construirGrafo: (
    actividadId: string,
    opciones: { podar?: boolean } = {},
  ): Promise<GrafoConstruido> =>
    apiFetch<GrafoConstruido>(
      `/validaciones/${actividadId}/aristas${query({ podar: opciones.podar })}`,
      { method: 'POST' },
    ),

  /**
   * Pide la síntesis del LOTE ENTERO: una corrida para toda la validación (D25).
   * Doble clic incluido — la llave de idempotencia es el contenido del lote, así
   * que pedirla otra vez sin comentarios nuevos devuelve la corrida anterior en
   * vez de cobrar otra.
   */
  sintetizar: (actividadId: string): Promise<EstadoCorridaSintesis> =>
    apiFetch<EstadoCorridaSintesis>(`/validaciones/${actividadId}/sintetizar`, {
      method: 'POST',
    }),

  /** El estado de la corrida mientras se espera. Nunca devuelve 500. */
  estadoCorrida: (corridaId: string): Promise<EstadoCorridaSintesis> =>
    apiFetch<EstadoCorridaSintesis>(`/ai/corridas/${corridaId}`),

  /**
   * El diff con procedencia: las cuatro capas por cada párrafo que la síntesis
   * tocó. `null` si la validación no tiene documento.
   *
   * Filtrar por CLASE y no por estado: todas las filas nacen en `propuesto`,
   * contradicciones incluidas, justamente para que ninguna vista las esconda.
   */
  diff: (
    actividadId: string,
    filtros: { clase?: ClaseCambio; estado?: EstadoCambio } = {},
  ): Promise<DiffConCambios | null> =>
    nullEn404(
      apiFetch<DiffConCambios>(
        `/validaciones/${actividadId}/cambios${query({
          clase: filtros.clase,
          estado: filtros.estado,
        })}`,
      ),
    ),

  /**
   * Aprobar, descartar o dejar en discusión. **Aprobar no es guardar un texto:
   * es contraer deudas** — se recorre el grafo y se abren los impactos de los
   * párrafos que quedan tocados. Es idempotente: aprobar dos veces no duplica
   * impactos ni reabre los que alguien ya resolvió.
   */
  decidir: (cambioId: string, decision: DecisionCambio): Promise<ResultadoDecision> =>
    apiFetch<ResultadoDecision>(`/cambios/${cambioId}`, { method: 'PATCH', json: decision }),

  /** Los impactos abiertos, agrupados por el cambio que los originó. */
  impactos: (
    actividadId: string,
    opciones: { soloPendientes?: boolean } = {},
  ): Promise<ImpactosDeValidacion | null> =>
    nullEn404(
      apiFetch<ImpactosDeValidacion>(
        `/validaciones/${actividadId}/impactos${query({
          solo_pendientes: opciones.soloPendientes,
        })}`,
      ),
    ),

  /**
   * Cerrar la deuda, con nombre y —si no se cambia nada— con razón escrita.
   * `revisado_sin_cambio` sin razón devuelve 422, y eso no es burocracia: dentro
   * de un año el expediente tiene que poder decir que alguien miró ese párrafo y
   * decidió dejarlo como estaba, y por qué.
   */
  resolverImpacto: (impactoId: string, cuerpo: ResolucionImpacto): Promise<ImpactoAbierto> =>
    apiFetch<ImpactoAbierto>(`/impactos/${impactoId}`, { method: 'PATCH', json: cuerpo }),
};

// ═══════════════════════════════════════════════════════════════════════════
// 8 · Vocabulario de pantalla
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Cómo se llama cada clase EN LA SALA. «Convergente» es jerga de analista; lo
 * que la gente entiende es «todos piden lo mismo».
 */
export const ETIQUETA_CLASE: Record<ClaseCambio, string> = {
  convergente: 'Todos piden lo mismo',
  complementaria: 'Se complementan',
  contradictoria: 'Posiciones enfrentadas',
  sin_cambio: 'Sin cambio propuesto',
};

/** La línea de ayuda de cada clase: qué significa para quien tiene que decidir. */
export const AYUDA_CLASE: Record<ClaseCambio, string> = {
  convergente: 'Las voces coinciden. La redacción de abajo recoge lo que pidieron.',
  complementaria: 'Cada quien aportó una parte. La redacción de abajo las fusiona.',
  contradictoria:
    'La inteligencia artificial no resuelve esto, y hace bien: promediar dos posturas ' +
    'opuestas inventa un acuerdo que nadie sostuvo. Elegí una postura o escribí la tuya.',
  sin_cambio: 'Se habló del párrafo pero nadie pidió cambiarlo.',
};

export const ETIQUETA_ESTADO: Record<EstadoCambio, string> = {
  propuesto: 'Propuesto',
  en_discusion: 'En discusión',
  acordado: 'Acordado',
  aprobado: 'Aprobado',
  aplicado: 'Aplicado al documento',
  descartado: 'Descartado',
};

export const ETIQUETA_QUE_APORTA: Record<QueAporta, string> = {
  pidio: 'lo pidió',
  respalda: 'lo respalda',
  contradice: 'lo contradice',
  pregunta: 'preguntó',
};

export const ETIQUETA_ESTADO_IMPACTO: Record<EstadoImpacto, string> = {
  pendiente: 'Sin revisar',
  revisado_sin_cambio: 'Revisado, sin cambio',
  genera_cambio_derivado: 'Generó otro cambio',
  descartado: 'Descartado',
};

/** Una contradicción NO trae propuesta, y eso es correcto. */
export function esContradiccion(cambio: CambioConProcedencia): boolean {
  return cambio.clase === 'contradictoria';
}

/** La sala ya decidió. Se puede volver sobre ello, salvo que ya esté aplicado. */
export function yaDecidido(cambio: CambioConProcedencia): boolean {
  return (
    cambio.estado === 'aprobado' ||
    cambio.estado === 'aplicado' ||
    cambio.estado === 'descartado'
  );
}

/**
 * Escrito en el documento: no se re-decide desde acá. Si hay que corregirlo, se
 * corrige en la versión siguiente. El servidor devuelve 409 sobre estos.
 */
export function esTerminal(cambio: CambioConProcedencia): boolean {
  return cambio.estado === 'aplicado';
}

/**
 * Por qué NO se puede aprobar todavía, o cadena vacía si se puede.
 *
 * Esto **no inventa una regla nueva**: repite la del servidor
 * (`sintesis_bloque.puede_aprobarse`) con el único dato que el servidor todavía
 * no tiene — la redacción que la persona está escribiendo en este momento y que
 * va a viajar en el mismo PATCH.
 *
 * Sin ese matiz la pantalla se traba justo donde más importa: una contradicción
 * llega con `puede_aprobarse: false` porque no tiene texto final, quien facilita
 * elige una postura, y el botón seguiría deshabilitado para siempre porque la
 * bandera es de la respuesta anterior. Las otras dos razones —que el párrafo no
 * tenga un cambio que aprobar, y que sea una propuesta de IA sin ninguna voz que
 * la respalde (C7)— NO las arregla escribir, y por eso siguen bloqueando.
 *
 * Los mensajes son los del servidor, tal cual: dos textos distintos para la
 * misma regla terminan divergiendo, y el día que diverjan la pantalla va a estar
 * mintiendo.
 */
export function bloqueoParaAprobar(cambio: CambioConProcedencia, borrador: string): string {
  const texto = borrador.trim();

  if (cambio.clase === 'sin_cambio') {
    return (
      cambio.porque_no ||
      'Este párrafo no tiene un cambio que aprobar: lo que se comentó no pedía tocar el texto.'
    );
  }
  if (cambio.no_respaldado) {
    return (
      cambio.porque_no ||
      'Ninguna cita de esta propuesta resistió la validación: no hay nadie de la sala ' +
        'pidiendo esto. Anclá el comentario que lo respalda o escribí el cambio a tu nombre.'
    );
  }
  if (!texto) {
    if (esContradiccion(cambio)) {
      return (
        cambio.porque_no ||
        'Acá hay posturas enfrentadas y el sistema no las resuelve. Elegí una de las ' +
          'posiciones y escribí el texto final: la decisión queda a tu nombre.'
      );
    }
    if (!(cambio.final ?? cambio.propuesto ?? '').trim()) {
      return (
        cambio.porque_no ||
        'No hay texto que aprobar todavía. Escribí la redacción final antes de aprobar.'
      );
    }
    // Cualquier otra negativa del servidor que no dependa de lo escrito.
    if (!cambio.puede_aprobarse) return cambio.porque_no;
  }
  return '';
}

/** Cómo se nombra un párrafo en la sala. Un uuid no le dice nada a nadie. */
export function etiquetaBloque(ref: string | null, orden: number | null): string {
  if (ref) return ref;
  return orden != null ? `¶ ${orden}` : 'Párrafo';
}

/**
 * El texto que le corresponde al párrafo según se mire el documento ANTES o
 * DESPUÉS. «Después» sin nada aprobado ni propuesto es, correctamente, el
 * original: no hay cambio que mostrar.
 */
export function textoSegunVista(
  cambio: CambioConProcedencia,
  vista: 'antes' | 'despues',
): string {
  if (vista === 'antes') return cambio.original;
  return cambio.final ?? cambio.propuesto ?? cambio.original;
}

/** El borrador con el que arranca la capa 4: lo aprobado, o lo propuesto. */
export function borradorInicial(cambio: CambioConProcedencia): string {
  return cambio.final ?? cambio.propuesto ?? '';
}

/** Índice `id_estable -> cambio` para marcar el documento sin recorrerlo N veces. */
export function indiceCambiosPorBloque(
  cambios: CambioConProcedencia[] | null | undefined,
): Map<string, CambioConProcedencia> {
  const m = new Map<string, CambioConProcedencia>();
  for (const c of cambios ?? []) {
    // Si dos cambios cayeran en el mismo párrafo gana el más reciente: es el que
    // la sala está mirando.
    m.set(c.bloque_id_estable, c);
  }
  return m;
}

/** Los impactos de un cambio, o null si ese cambio todavía no abrió ninguno. */
export function impactosDe(
  grupos: GrupoDeImpactos[] | null | undefined,
  cambioId: string,
): GrupoDeImpactos | null {
  return (grupos ?? []).find((g) => g.cambio_id === cambioId) ?? null;
}

// ═══════════════════════════════════════════════════════════════════════════
// 9 · Diferencia palabra por palabra
// ═══════════════════════════════════════════════════════════════════════════

export type TipoSegmento = 'igual' | 'quitado' | 'agregado';

export interface SegmentoDiff {
  tipo: TipoSegmento;
  texto: string;
}

/** Tope de palabras por lado. Un párrafo real no llega ni cerca; un pegote sí. */
const TOPE_DIFF = 1200;

function palabras(texto: string): string[] {
  // Se parte conservando el separador para poder reconstruir el texto tal cual:
  // un diff que se come los espacios obliga a leer dos veces para entender qué
  // cambió, que es justo lo contrario de lo que un diff sirve.
  return texto.match(/\S+\s*/g) ?? [];
}

function empujar(salida: SegmentoDiff[], tipo: TipoSegmento, texto: string): void {
  if (!texto) return;
  const ultimo = salida[salida.length - 1];
  if (ultimo && ultimo.tipo === tipo) ultimo.texto += texto;
  else salida.push({ tipo, texto });
}

/**
 * Diferencia palabra por palabra entre el texto original y el final.
 *
 * Determinista y del lado del cliente: comparar dos textos NO es trabajo de un
 * modelo, y por regla dura del proyecto ningún agregado mecánico entra a la cola
 * de IA. Es una subsecuencia común más larga clásica, con tope de tamaño: sobre
 * dos pegotes de miles de palabras la tabla sería de millones de celdas y
 * congelaría la pestaña, así que en ese caso se devuelve el bloque entero
 * quitado y el bloque entero agregado, que sigue siendo verdad.
 */
export function diffPalabras(original: string, final: string): SegmentoDiff[] {
  const a = palabras(original);
  const b = palabras(final);

  if (a.length === 0 && b.length === 0) return [];
  if (original === final) return [{ tipo: 'igual', texto: original }];
  if (a.length > TOPE_DIFF || b.length > TOPE_DIFF) {
    const salida: SegmentoDiff[] = [];
    empujar(salida, 'quitado', original);
    empujar(salida, 'agregado', final);
    return salida;
  }

  // Tabla de subsecuencia común más larga sobre palabras normalizadas: dos
  // palabras que solo difieren en mayúsculas o en la puntuación pegada no son
  // «un cambio» para quien lee.
  const na = a.map(normalizarPalabra);
  const nb = b.map(normalizarPalabra);
  const filas = a.length + 1;
  const cols = b.length + 1;
  const tabla = new Uint32Array(filas * cols);
  for (let i = a.length - 1; i >= 0; i--) {
    for (let j = b.length - 1; j >= 0; j--) {
      tabla[i * cols + j] =
        na[i] === nb[j]
          ? tabla[(i + 1) * cols + (j + 1)] + 1
          : Math.max(tabla[(i + 1) * cols + j], tabla[i * cols + (j + 1)]);
    }
  }

  const salida: SegmentoDiff[] = [];
  let i = 0;
  let j = 0;
  while (i < a.length && j < b.length) {
    if (na[i] === nb[j]) {
      // Gana la redacción nueva: es la que va a quedar en el documento.
      empujar(salida, 'igual', b[j]);
      i++;
      j++;
    } else if (tabla[(i + 1) * cols + j] >= tabla[i * cols + (j + 1)]) {
      empujar(salida, 'quitado', a[i]);
      i++;
    } else {
      empujar(salida, 'agregado', b[j]);
      j++;
    }
  }
  while (i < a.length) empujar(salida, 'quitado', a[i++]);
  while (j < b.length) empujar(salida, 'agregado', b[j++]);
  return salida;
}

function normalizarPalabra(p: string): string {
  return p.trim().toLowerCase().replace(/[.,;:()«»"']/g, '');
}

/** Cuántas palabras se quitaron y cuántas entraron. Para el resumen de la capa 4. */
export function resumenDiff(segmentos: SegmentoDiff[]): { quitadas: number; agregadas: number } {
  let quitadas = 0;
  let agregadas = 0;
  for (const s of segmentos) {
    const n = (s.texto.match(/\S+/g) ?? []).length;
    if (s.tipo === 'quitado') quitadas += n;
    else if (s.tipo === 'agregado') agregadas += n;
  }
  return { quitadas, agregadas };
}
